import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TrackByFunction,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SplitButtonModule } from 'primeng/splitbutton';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CustomCodeEventDetail } from '../interfaces/reassignment.interface';
import { TableColumnConfig } from '../interfaces/table-builder.interface';
import { PUITranslatePipe } from '../pipes/p-ui-translate.pipe';
import { TranslateConfigPipe } from '../pipes/translate-config.pipe';
import { ActionButtonConfig } from '../services/table-config-builder.service';
import { PaginationComponent } from './pagination.component';
import { SearchComponent } from './search.component';

export interface PaginationOption {
    value: number;
    viewValue: string;
}

export interface TableRow {
    selected?: boolean;
    [key: string]: any;
}

export interface TableSelectionEvent {
    selectedRows: TableRow[];
    allSelected: boolean;
}

@Component({
    selector: 'platform-table',
    standalone: true,
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        CheckboxModule,
        FormsModule,
        PaginationComponent,
        SearchComponent,
        SplitButtonModule,
        TooltipModule,
        PUITranslatePipe,
        TranslateConfigPipe,
    ],
    template: `
        <div class="flex align-items-center justify-content-between search-pagination">
            <platform-search
                #searchRef
                [searchQuery]="searchQuery"
                (searchChange)="onSearchChange($event)"
                [enableRoundedCorners]="true"
            >
            </platform-search>
            <div class="flex align-items-center gap-3">
                <platform-pagination
                    [pageSizeOptions]="pageSizeOptions"
                    [totalItems]="filteredData.length"
                    [currentPage]="currentPage"
                    (pageChange)="onPageChange($event)"
                    (pageSizeChange)="onPageSizeChange($event)"
                    [selectedPageSize]="pageSize"
                >
                </platform-pagination>
            </div>
        </div>

        <div class="border-1 surface-border overflow-hidden">
            <p-table
                #tableRef
                [value]="visibleData"
                [(selection)]="selectedRows"
                [ngClass]="customClass"
                [scrollable]="true"
                [rowTrackBy]="rowTrackBy"
                class="scrollable-thead"
            >
                <ng-template pTemplate="header">
                    <tr class="header-container">
                        @if (selectable) {
                            <th style="width: 4rem">
                                <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
                            </th>
                        }
                        @for (column of tableConfiguration; track column) {
                            <th
                                [hidden]="column.hide"
                                class="frozen-cell"
                                pFrozenColumn
                                [alignFrozen]="column.alignFrozen || 'left'"
                                class="frozen-cell"
                                [frozen]="column.frozen || false"
                            >
                                {{ column.columnName | translateConfig: column.columnName }}
                            </th>
                        }
                        @if (enableAction) {
                            <th
                                pFrozenColumn
                                [frozen]="actionButtonConfig?.frozen || false"
                                alignFrozen="right"
                                class="frozen-cell"
                            >
                                {{ actionButtonConfig?.columnLabel | pTranslate: 'Bulk Action' }}
                            </th>
                        }
                    </tr>
                </ng-template>

                <ng-template pTemplate="body" let-rowData let-rowIndex="rowIndex">
                    <tr>
                        @if (selectable) {
                            <td style="width: 4rem">
                                <p-tableCheckbox [value]="rowData"></p-tableCheckbox>
                            </td>
                        }
                        @for (column of tableConfiguration; track column) {
                            <td
                                [hidden]="column.hide"
                                [ngClass]="column.cellClass"
                                [ngStyle]="column.cellStyle"
                                [attr.data-column-name]="column?.columnName"
                                [attr.data-row-index]="rowIndex - 2"
                                [attr.data-value]="rowData[column.mapping]"
                                pFrozenColumn
                                class="frozen-cell"
                                [alignFrozen]="column.alignFrozen || 'left'"
                                class="frozen-cell"
                                [frozen]="column.frozen || false"
                            >
                                <div class="flex gap-2 align-items-center">
                                    <span> {{ rowData[column.mapping] }}</span>
                                    @if (column.tooltipText) {
                                        <i
                                            class="pi pi-exclamation-triangle"
                                            [ngClass]="column.tooltipClass"
                                            [pTooltip]="column.tooltipText | pTranslate: column.tooltipText"
                                            tooltipPosition="bottom"
                                        ></i>
                                    }
                                </div>
                            </td>
                        }

                        @if (enableAction) {
                            <td
                                pFrozenColumn
                                class="frozen-cell"
                                [ngClass]="actionButtonConfig?.cellClass"
                                [ngStyle]="actionButtonConfig?.cellStyle"
                                [frozen]="actionButtonConfig?.frozen || false"
                            >
                                <!-- Standard Button -->
                                @if (actionButtonConfig?.buttonType === 'button') {
                                    <p-button
                                        [label]="
                                            getActionButtonLabel(rowData, true)
                                                | pTranslate: getActionButtonLabel(rowData, true)
                                        "
                                        [outlined]="actionButtonConfig?.outlined"
                                        [iconPos]="actionButtonConfig?.iconPosition || 'right'"
                                        [icon]="actionButtonConfig?.icon"
                                        (onClick)="handleAction(rowData)"
                                        [ngClass]="'action-button'"
                                        [pTooltip]="
                                            getActionButtonLabel(rowData, false)
                                                | pTranslate: getActionButtonLabel(rowData, false)
                                        "
                                        tooltipPosition="left"
                                    >
                                    </p-button>
                                }

                                <!-- Split Button -->
                                @if (actionButtonConfig?.buttonType === 'splitButton') {
                                    <p-splitButton
                                        [label]="getActionButtonLabel(rowData, true) | pTranslate"
                                        [icon]="actionButtonConfig?.icon"
                                        [iconPos]="actionButtonConfig?.iconPosition || 'right'"
                                        [model]="actionButtonConfig?.menuItems"
                                        (onClick)="handleAction(rowData)"
                                        [ngClass]="'action-button'"
                                        [pTooltip]="getActionButtonLabel(rowData, false) | pTranslate"
                                        tooltipPosition="left"
                                    >
                                    </p-splitButton>
                                }
                            </td>
                        }
                    </tr>
                </ng-template>
            </p-table>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            :host ::ng-deep .scrollable-thead thead,
            :host ::ng-deep .frozen-cell {
                z-index: 0 !important;
            }

            .action-button {
                display: inline-block;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                // width: "100%"
            }
            .search-pagination {
                padding: 0.75rem 1.25rem;
            }
            .header-container {
                font-size: 0.875rem !important;
            }

            .color-warning {
                color: var(--alpha-warning-color, #f7900);
            }
        `,
    ],
})
export class TableComponent implements AfterViewInit, OnInit {
    filteredData: TableRow[] = [];
    private _tc: TableColumnConfig[] = [];
    @Input() set tableConfiguration(v: Array<TableColumnConfig>) {
        this._tc = v;
        if (this.searchComponent) this.searchComponent.searchQuery = '';
        this.preprocessData();
    }
    get tableConfiguration(): Array<TableColumnConfig> {
        return this._tc;
    }
    visibleData: TableRow[] = [];
    @Input() set data(value: TableRow[]) {
        this._data = value;
        this.filteredData = [...value];
        this.preprocessData();
    }
    get data(): TableRow[] {
        return this._data;
    }
    private _data: TableRow[] = [];
    @Input() reorderable = true;
    @Input() selectable = true;

    // Pagination & Search
    @Input() searchQuery = '';

    // Action Button Configuration
    @Input() enableAction = false;
    @Input() enableBulkAction = false;
    @Input() bulkButtonConfig: ActionButtonConfig | undefined;
    @Input() actionButtonConfig: ActionButtonConfig | undefined;
    @Input() clearAllButtonConfig: ActionButtonConfig | undefined;
    @Input() customClass: string | string[] = '';
    @Input() enableClearAllAction = false;

    // optimaizations
    @Input() rowTrackBy: TrackByFunction<TableRow> = (index: number, rowData: TableRow) => {
        return rowData;
    };

    // Selected Rows
    selectedRows: TableRow[] = [];

    // Events
    @Output() onReorder = new EventEmitter<any>();
    @Output() onAction = new EventEmitter<any>();
    @Output() onBulkAction = new EventEmitter<any>();
    @Output() onSelect = new EventEmitter<TableSelectionEvent>();
    @Output() pageChange = new EventEmitter<number>();
    @Output() pageSizeChange = new EventEmitter<number>();
    @Output() afterViewInitEvent = new EventEmitter<CustomCodeEventDetail>();
    @Output() onClearAllAction = new EventEmitter<any>();

    @ViewChild('tableRef') tableRef!: Table;
    @ViewChild('searchRef') searchComponent!: SearchComponent;

    allSelected = false;
    private preprocessedData: Map<TableRow, string> = new Map();

    get totalCount(): number {
        return (this._data || []).length;
    }

    onSearchChange(searchQuery: string) {
        if (!searchQuery) {
            this.filteredData = [...this.data];
        } else {
            const lowerCaseQuery = searchQuery.toLowerCase();

            this.filteredData = this.data.filter((row) => {
                const searchableString = this.preprocessedData.get(row) || '';
                console.log('searchableString.includes(lowerCaseQuery)', searchableString.includes(lowerCaseQuery));
                return searchableString.includes(lowerCaseQuery);
            });
        }
        this.totalItems = this.filteredData.length;
        this.updateVisibleData();
        console.log('this.toalItems', this.totalItems, this.filteredData.length, JSON.stringify(this.filteredData));
    }

    handleAction(rowData: TableRow) {
        this.onAction.emit(rowData);
    }

    preprocessData() {
        this.preprocessedData = new Map<TableRow, string>();
        this.data.forEach((row) => {
            const searchableString = this.tableConfiguration
                .map((column) => row[column.mapping]?.toString().toLowerCase() || '')
                .join(' ');
            this.preprocessedData.set(row, searchableString);
        });
        this.totalItems = this.filteredData.length;
        this.initializePagination();
    }

    getActionButtonLabel(rowData: TableRow, trim = false): string {
        if (this.actionButtonConfig?.labelMapping && rowData[this.actionButtonConfig.labelMapping]) {
            return trim
                ? rowData[this.actionButtonConfig.labelMapping].substring(0, 5) + '..'
                : rowData[this.actionButtonConfig.labelMapping];
        }
        return this.actionButtonConfig?.buttonLabel || 'Button';
    }
    private readonly DEFAULT_PAGE_SIZES: PaginationOption[] = [
        { value: 10, viewValue: '10 Rows' },
        { value: 25, viewValue: '25 Rows' },
        { value: 50, viewValue: '50 Rows' },
        { value: 100, viewValue: '100 Rows' },
    ];
    private readonly DEFAULT_CURRENT_PAGE = 1;
    private _pageSizeOptions: PaginationOption[] = this.DEFAULT_PAGE_SIZES;
    @Input()
    set pageSizeOptions(value: PaginationOption[]) {
        this._pageSizeOptions = value?.length ? value : this.DEFAULT_PAGE_SIZES;
    }
    get pageSizeOptions(): PaginationOption[] {
        return this._pageSizeOptions;
    }

    private _currentPage: number = this.DEFAULT_CURRENT_PAGE;
    @Input()
    set currentPage(value: number) {
        this._currentPage = value || this.DEFAULT_CURRENT_PAGE;
        if (this._data) {
            this.updateVisibleData();
        }
    }
    get currentPage(): number {
        return this._currentPage;
    }

    private _pageSize: number = this.DEFAULT_PAGE_SIZES[0].value;
    @Input()
    set pageSize(value: number) {
        this._pageSize = value || this.DEFAULT_PAGE_SIZES[0].value;
        if (this._data) {
            this.updateVisibleData();
        }
    }
    get pageSize(): number {
        return this._pageSize;
    }

    private _totalItems = 0;
    @Input()
    set totalItems(value: number) {
        this._totalItems = Math.max(0, value || 0);
    }
    get totalItems(): number {
        return this._totalItems;
    }

    ngOnInit() {
        // Initialize with default values
        this.initializePagination();
    }

    ngAfterViewInit() {
        // Initialize with default values
        this.initializePagination();
        setTimeout(() => {
            this.afterViewInitEvent.emit({
                data: this.data,
                element: this.tableRef.el.nativeElement,
            });
        });
    }

    private initializePagination() {
        // Ensure we have valid pagination options
        if (!this._pageSizeOptions.length) {
            this._pageSizeOptions = this.DEFAULT_PAGE_SIZES;
        }

        // Ensure we have a valid page size
        if (!this._pageSize || this._pageSize <= 0) {
            this._pageSize = this.DEFAULT_PAGE_SIZES[0].value;
        }

        // Ensure we have a valid current page
        if (!this._currentPage || this._currentPage <= 0) {
            this._currentPage = this.DEFAULT_CURRENT_PAGE;
        }

        this.updateVisibleData();
    }

    private getTotalPages(): number {
        return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    }

    private updateVisibleData() {
        if (!Array.isArray(this._data)) {
            this.filteredData = [];
            return;
        }

        const totalPages = this.getTotalPages();

        // Ensure current page is within bounds
        if (this._currentPage > totalPages) {
            this._currentPage = totalPages;
        }
        const startIndex = (this._currentPage - 1) * this._pageSize;
        const endIndex = Math.min(startIndex + this._pageSize, this._data.length);

        // Ensure indices are valid numbers
        if (isNaN(startIndex) || isNaN(endIndex)) {
            this.filteredData = this._data;
            return;
        }

        this.visibleData = this.filteredData.slice(startIndex, endIndex);
    }

    onPageChange(page: number): void {
        if (page !== this._currentPage && !isNaN(page) && page > 0) {
            this._currentPage = Math.min(page, this.getTotalPages());
            this.pageChange.emit(this._currentPage);
            this.updateVisibleData();
        }
    }

    onPageSizeChange(newSize: number): void {
        if (newSize !== this._pageSize && !isNaN(newSize) && newSize > 0) {
            this._pageSize = newSize;
            // Reset to first page when changing page size
            this._currentPage = 1;
            this.pageSizeChange.emit(this._pageSize);
            this.pageChange.emit(this._currentPage);
            this.updateVisibleData();
        }
    }
}
